import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";
import MultiSelectDropdown from "./MultiSelectDropdown";

/**
 * Asistente de Pagos personalizado (estilo SAP B1).
 *
 * Pasos:
 *   1) Filtros generales y por documento/SN
 *   2) Recomendaciones (grid editable: Checked, PayAmount)
 *   3) Resumen, persistencia (Run) y ejecucion
 *
 * Endpoints backend:
 *   POST /payment-wizard/recommend
 *   POST /payment-wizard/runs
 *   POST /payment-wizard/runs/<code>/execute
 */

const STEP_LABELS = ["Parametros", "Recomendaciones", "Resumen y ejecucion"];

const initialFilters = {
  paymentType: "O",
  dueDateFrom: "",
  dueDateTo: "",
  docDateFrom: "",
  docDateTo: "",
  cardCodeFrom: "",
  cardCodeTo: "",
  cardCodes: "",
  excludeBP: "",
  groupCodes: [],        // array de strings (codigos)
  paymentMethods: [],    // array de strings
  currencies: [],        // array de strings
  bankCode: "",          // string unico
  minAmount: "",
  maxAmount: "",
  considerCreditNotes: true,
  onlyWithBank: true,
};

const initialHeader = {
  name: "",
  pmntDate: new Date().toISOString().slice(0, 10),
  pymCode: "",
  bankCode: "",
  bankAcct: "",         // GLAccount -> CashAccount en SL
  bankAccountNo: "",    // numero de cuenta bancaria (informativo)
  currency: "GS",
  notes: "",
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("es-PY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

function csvToList(s) {
  if (!s) return undefined;
  return s
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

const AsistentePagos = () => {
  const [step, setStep] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [run, setRun] = useState(null); // { code, status, ... } luego de crear
  const [execResult, setExecResult] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [view, setView] = useState("list"); // 'list' | 'wizard'
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsFilter, setRunsFilter] = useState({ status: "", docType: "" });
  const [catalogs, setCatalogs] = useState({
    groups: [],
    paymentMethods: [],
    currencies: [],
    banks: [],
  });

  // Cargar catalogos al cambiar tipo de pago
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await apiClient.get(
          `/payment-wizard/catalogs?paymentType=${filters.paymentType}`
        );
        if (!cancelled && data) setCatalogs(data);
      } catch (e) {
        console.warn("No se pudieron cargar catalogos", e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filters.paymentType]);

  // Si abrimos un run existente y no hay metodo seleccionado pero si bankAcct,
  // intentar deducir el metodo de pago a partir de la cuenta contable (glAccount).
  useEffect(() => {
    if (header.pymCode) return;
    if (!header.bankAcct) return;
    const methods = catalogs?.paymentMethods || [];
    if (!methods.length) return;
    const match = methods.find(
      (m) => String(m.glAccount || "") === String(header.bankAcct || "")
    );
    if (match) {
      setHeader((h) => ({
        ...h,
        pymCode: match.code,
        bankAccountNo: match.account || h.bankAccountNo,
      }));
    }
  }, [catalogs.paymentMethods, header.bankAcct, header.pymCode]);

  const onMultiChange = (field) => (e) => {
    const values = Array.from(e.target.selectedOptions).map((o) => o.value);
    setFilters((f) => ({ ...f, [field]: values }));
  };

  /* ---------------------------------------------------------------------- */
  /* Step 1 - Filtros                                                        */
  /* ---------------------------------------------------------------------- */
  const buildFiltersPayload = () => {
    const f = { ...filters };
    f.cardCodes = csvToList(filters.cardCodes);
    f.excludeBP = csvToList(filters.excludeBP);
    // groupCodes / paymentMethods / currencies ya son arrays de strings
    f.groupCodes = (filters.groupCodes || []).map((x) => Number(x)).filter(Boolean);
    f.paymentMethods = filters.paymentMethods || [];
    f.currencies = filters.currencies || [];
    f.minAmount = filters.minAmount === "" ? undefined : Number(filters.minAmount);
    f.maxAmount = filters.maxAmount === "" ? undefined : Number(filters.maxAmount);
    Object.keys(f).forEach((k) => {
      if (f[k] === "" || f[k] === undefined || (Array.isArray(f[k]) && f[k].length === 0))
        delete f[k];
    });
    return f;
  };

  const handleRecommend = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    setExecResult(null);
    try {
      const { data } = await apiClient.post(
        "/payment-wizard/recommend",
        buildFiltersPayload()
      );
      const list = (data?.items || []).map((r, idx) => ({
        ...r,
        _idx: idx,
        Checked: true,
        PayAmount: Number(r.OpenAmount || 0),
      }));
      setItems(list);
      setStep(1);
      // El detalle (X de Y) se muestra en el paso 2 sobre la grilla;
      // aqui solo confirmamos cuantos trajo la consulta.
      setInfo(
        list.length === 0
          ? "No se encontraron documentos para los filtros indicados."
          : ""
      );
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
          "Error obteniendo recomendaciones. Revise los filtros."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Step 2 - Recomendaciones                                                */
  /* ---------------------------------------------------------------------- */
  const toggleAll = (checked) =>
    setItems((arr) => arr.map((it) => ({ ...it, Checked: checked })));

  const updateLine = (idx, patch) =>
    setItems((arr) => arr.map((it) => (it._idx === idx ? { ...it, ...patch } : it)));

  const totals = useMemo(() => {
    const sel = items.filter((i) => i.Checked);
    const total = sel.reduce((s, i) => s + Number(i.PayAmount || 0), 0);
    const cards = new Set(sel.map((i) => i.CardCode)).size;
    return { lines: sel.length, total, cards };
  }, [items]);

  // Agrupar por CardCode (proveedor / cliente)
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = it.CardCode || "";
      if (!map.has(key)) {
        map.set(key, {
          cardCode: key,
          cardName: it.CardName || "",
          bankCode: it.BankCode || "",
          bankAcct: it.BankAcct || "",
          rows: [],
          total: 0,
          open: 0,
          pay: 0,
          checkedCount: 0,
        });
      }
      const g = map.get(key);
      g.rows.push(it);
      g.total += Number(it.DocTotal || 0);
      g.open += Number(it.OpenAmount || 0);
      g.pay += it.Checked ? Number(it.PayAmount || 0) : 0;
      if (it.Checked) g.checkedCount += 1;
    });
    return Array.from(map.values()).sort((a, b) =>
      a.cardCode.localeCompare(b.cardCode)
    );
  }, [items]);

  const toggleGroup = (cardCode) =>
    setCollapsedGroups((c) => ({ ...c, [cardCode]: !c[cardCode] }));

  const toggleAllGroups = (collapse) => {
    const next = {};
    groups.forEach((g) => {
      next[g.cardCode] = collapse;
    });
    setCollapsedGroups(next);
  };

  const toggleGroupChecked = (cardCode, checked) =>
    setItems((arr) =>
      arr.map((it) =>
        it.CardCode === cardCode ? { ...it, Checked: checked } : it
      )
    );

  /* ---------------------------------------------------------------------- */
  /* Step 3 - Crear Run y ejecutar                                           */
  /* ---------------------------------------------------------------------- */
  const handleCreateRun = async (status = "DRAFT") => {
    if (totals.lines === 0) {
      setError("Debe seleccionar al menos una linea.");
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const lines = items.map((i) => ({
        CardCode: i.CardCode,
        CardName: i.CardName,
        DocEntry: i.DocEntry,
        DocNum: i.DocNum,
        ObjType: i.ObjType,
        DocDate: i.DocDate,
        DueDate: i.DueDate,
        DocCur: i.DocCur,
        DocTotal: i.DocTotal,
        OpenAmount: i.OpenAmount,
        PayAmount: Number(i.PayAmount || 0),
        Checked: !!i.Checked,
        PymCode: i.PymCode,
        BankCode: i.BankCode,
        BankBranch: i.BankBranch,
        BankAcct: i.BankAcct,
        BankSwift: i.BankSwift,
      }));

      const payload = {
        name: header.name || `PROC-${Date.now()}`,
        pmntDate: header.pmntDate,
        docType: filters.paymentType,
        status,
        pymCode: header.pymCode,
        bankCode: header.bankCode,
        bankAcct: header.bankAcct,
        currency: header.currency,
        notes: header.notes,
        filters: buildFiltersPayload(),
        createdBy: localStorage.getItem("sapUser")
          ? JSON.parse(localStorage.getItem("sapUser") || "{}").UserName || ""
          : "",
        lines,
      };

      const { data } = await apiClient.post("/payment-wizard/runs", payload);
      setRun(data);
      setInfo(
        status === "RECOMMENDED"
          ? `Recomendacion ${data.code} guardada con ${data.linesCreated || data.lineCount} lineas (Total ${fmt(data.totalAmt)}). Lista para aprobar y ejecutar.`
          : `Run ${data.code} creado con ${data.linesCreated || data.lineCount} lineas (Total ${fmt(data.totalAmt)}).`
      );
      setStep(2);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Error creando el run.");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!run?.code) return;
    if (!window.confirm(`Ejecutar el run ${run.code}? Se crearan los pagos en SAP.`))
      return;
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.post(
        `/payment-wizard/runs/${run.code}/execute`,
        {
          user: JSON.parse(localStorage.getItem("sapUser") || "{}").UserName || "",
        }
      );
      setExecResult(data);
      setRun((r) => ({ ...r, status: data.status }));
      setInfo(
        `Ejecucion finalizada: ${data.created} pagos creados, ${data.failed} con error.`
      );
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Error ejecutando el run.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setStep(0);
    setItems([]);
    setRun(null);
    setExecResult(null);
    setError("");
    setInfo("");
  };

  /* ---------------------------------------------------------------------- */
  /* Listado de runs                                                         */
  /* ---------------------------------------------------------------------- */
  const fetchRuns = async () => {
    setRunsLoading(true);
    try {
      const params = new URLSearchParams();
      if (runsFilter.status) params.append("status", runsFilter.status);
      if (runsFilter.docType) params.append("docType", runsFilter.docType);
      const qs = params.toString();
      const { data } = await apiClient.get(
        `/payment-wizard/runs${qs ? `?${qs}` : ""}`
      );
      setRuns(Array.isArray(data) ? data : data?.items || data?.value || []);
    } catch (e) {
      console.warn("No se pudo cargar el listado de runs", e);
    } finally {
      setRunsLoading(false);
    }
  };

  useEffect(() => {
    if (view === "list") fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, runsFilter.status, runsFilter.docType]);

  const startNewWizard = () => {
    resetAll();
    setFilters(initialFilters);
    setHeader(initialHeader);
    setView("wizard");
  };

  const backToList = () => {
    resetAll();
    setView("list");
  };

  const STATUS_BADGE = {
    DRAFT: "secondary",
    RECOMMENDED: "info",
    EXECUTING: "warning",
    EXECUTED: "success",
    EXPORTED: "primary",
    CANCELED: "danger",
  };

  const STATUS_LABEL = {
    DRAFT: "Borrador",
    RECOMMENDED: "Recomendado",
    EXECUTING: "En ejecucion",
    EXECUTED: "Ejecutado",
    EXPORTED: "Exportado",
    CANCELED: "Cancelado",
  };

  const openRun = async (r) => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const { data } = await apiClient.get(
        `/payment-wizard/runs/${r.Code}`
      );
      const linesRaw = data?.Lines || [];
      const lineItems = linesRaw.map((l, idx) => ({
        _idx: idx,
        _runLineCode: l.Code,
        _paymentStat: l.U_PaymentStat || "PENDING",
        _paymentDocE: l.U_PaymentDocE,
        _paymentDocN: l.U_PaymentDocN,
        _errorMsg: l.U_ErrorMsg,
        CardCode: l.U_CardCode,
        CardName: l.U_CardName,
        DocEntry: l.U_DocEntry,
        DocNum: l.U_DocNum,
        ObjType: l.U_ObjType,
        DocDate: l.U_DocDate,
        DueDate: l.U_DueDate,
        DocCur: l.U_DocCur,
        DocTotal: Number(l.U_DocTotal || 0),
        OpenAmount: Number(l.U_OpenAmount || 0),
        PayAmount: Number(l.U_PayAmount || 0),
        Checked: l.U_Checked === "Y",
        PymCode: l.U_PymMeth,
        BankCode: l.U_BankCode,
        BankBranch: l.U_BankBranch,
        BankAcct: l.U_BankAcct,
        BankSwift: l.U_BankSwift,
      }));
      setItems(lineItems);
      setRun({
        code: data.Code,
        status: data.U_Status,
        name: data.U_RunName || data.Name,
      });
      setHeader({
        ...initialHeader,
        name: data.U_RunName || data.Name || "",
        pmntDate:
          (data.U_PmntDate || "").slice(0, 10) || initialHeader.pmntDate,
        bankCode: data.U_BankCode || "",
        bankAcct: data.U_BankAcct || "",
        currency: data.U_Currency || "GS",
        notes: data.U_Notes || "",
      });
      // Restaurar filtros guardados (incluye paymentType)
      let savedFilters = {};
      try {
        savedFilters = data.U_Filters
          ? JSON.parse(data.U_Filters)
          : {};
      } catch (_) {
        savedFilters = {};
      }
      setFilters({
        ...initialFilters,
        ...savedFilters,
        paymentType: data.U_DocType || savedFilters.paymentType || "O",
      });
      setStep(1); // entrar directo a la grilla
      setView("wizard");
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.error || "No se pudo abrir el asistente."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!run?.code) return;
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const lines = items
        .filter((i) => i._runLineCode)
        .map((i) => ({
          Code: i._runLineCode,
          checked: !!i.Checked,
          payAmount: Number(i.PayAmount || 0),
        }));
      const { data } = await apiClient.patch(
        `/payment-wizard/runs/${run.code}`,
        { lines }
      );
      const updated = data?.lines?.updated ?? lines.length;
      const total = data?.lines?.totalAmt;
      setInfo(
        `Cambios guardados: ${updated} lineas actualizadas${
          total !== undefined ? ` (Total ${fmt(total)})` : ""
        }.`
      );
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error || "Error guardando los cambios."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ---------------------------------------------------------------------- */
  return (
    <div className="container-fluid">
      <h3 className="mb-3">Asistente de Pagos</h3>

      {view === "list" && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="mb-0">Asistentes existentes</h5>
              <div className="d-flex gap-2 flex-wrap">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 160 }}
                  value={runsFilter.docType}
                  onChange={(e) =>
                    setRunsFilter({ ...runsFilter, docType: e.target.value })
                  }
                >
                  <option value="">Tipo (todos)</option>
                  <option value="O">Pagos a proveedores</option>
                  <option value="I">Cobros de clientes</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 180 }}
                  value={runsFilter.status}
                  onChange={(e) =>
                    setRunsFilter({ ...runsFilter, status: e.target.value })
                  }
                >
                  <option value="">Estado (todos)</option>
                  <option value="DRAFT">Borrador</option>
                  <option value="RECOMMENDED">Recomendado</option>
                  <option value="EXECUTING">En ejecucion</option>
                  <option value="EXECUTED">Ejecutado</option>
                  <option value="EXPORTED">Exportado</option>
                  <option value="CANCELED">Cancelado</option>
                </select>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={fetchRuns}
                  disabled={runsLoading}
                >
                  {runsLoading ? "Cargando..." : "Refrescar"}
                </button>
                <button
                  className="btn btn-sm btn-success"
                  onClick={startNewWizard}
                >
                  + Nuevo asistente
                </button>
              </div>
            </div>

            <div style={{ maxHeight: "65vh", overflow: "auto" }}>
              <table className="table table-sm table-striped table-hover align-middle">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Codigo</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Fecha pago</th>
                    <th>Estado</th>
                    <th className="text-end">Lineas</th>
                    <th className="text-end">Total</th>
                    <th>Moneda</th>
                    <th>Creado por</th>
                    <th>Creado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.Code}>
                      <td>
                        <code>{r.Code}</code>
                      </td>
                      <td>{r.U_RunName || r.Name}</td>
                      <td>
                        {r.U_DocType === "O"
                          ? "Pago"
                          : r.U_DocType === "I"
                          ? "Cobro"
                          : r.U_DocType}
                      </td>
                      <td>{(r.U_PmntDate || "").slice(0, 10)}</td>
                      <td>
                        <span
                          className={`badge bg-${
                            STATUS_BADGE[r.U_Status] || "secondary"
                          }`}
                        >
                          {STATUS_LABEL[r.U_Status] || r.U_Status}
                        </span>
                      </td>
                      <td className="text-end">{r.U_LineCount || 0}</td>
                      <td className="text-end">{fmt(r.U_TotalAmt)}</td>
                      <td>{r.U_Currency}</td>
                      <td>{r.U_CreatedBy}</td>
                      <td>{(r.U_CreatedAt || "").slice(0, 10)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={loading}
                          onClick={() => openRun(r)}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                  {runs.length === 0 && !runsLoading && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted">
                        No hay asistentes registrados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {view === "wizard" && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={backToList}
            >
              &laquo; Volver al listado
            </button>
          </div>

          {/* Stepper */}
          <ul className="nav nav-pills mb-3">
            {STEP_LABELS.map((lbl, i) => (
              <li key={lbl} className="nav-item">
                <button
                  className={`nav-link ${step === i ? "active" : ""}`}
                  disabled={i > step}
                  onClick={() => setStep(i)}
                >
                  {i + 1}. {lbl}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {view === "wizard" && error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}
      {view === "wizard" && info && (
        <div className="alert alert-info py-2">{info}</div>
      )}

      {/* ---------- Step 0: filtros ---------- */}
      {view === "wizard" && step === 0 && (
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Tipo de pago</label>
                <select
                  className="form-select"
                  value={filters.paymentType}
                  onChange={(e) =>
                    setFilters({ ...filters, paymentType: e.target.value })
                  }
                >
                  <option value="O">Pagos a proveedores</option>
                  <option value="I">Cobros de clientes</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Vto. desde</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.dueDateFrom}
                  onChange={(e) =>
                    setFilters({ ...filters, dueDateFrom: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Vto. hasta</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.dueDateTo}
                  onChange={(e) =>
                    setFilters({ ...filters, dueDateTo: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Banco SN</label>
                <select
                  className="form-select"
                  value={filters.bankCode}
                  onChange={(e) =>
                    setFilters({ ...filters, bankCode: e.target.value })
                  }
                >
                  <option value="">(todos)</option>
                  {catalogs.banks.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.code} - {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Codigo SN desde</label>
                <input
                  className="form-control"
                  value={filters.cardCodeFrom}
                  onChange={(e) =>
                    setFilters({ ...filters, cardCodeFrom: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Codigo SN hasta</label>
                <input
                  className="form-control"
                  value={filters.cardCodeTo}
                  onChange={(e) =>
                    setFilters({ ...filters, cardCodeTo: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Codigos SN (lista)</label>
                <input
                  className="form-control"
                  value={filters.cardCodes}
                  onChange={(e) =>
                    setFilters({ ...filters, cardCodes: e.target.value })
                  }
                  placeholder="P00023,P00320"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Excluir codigos SN</label>
                <input
                  className="form-control"
                  value={filters.excludeBP}
                  onChange={(e) =>
                    setFilters({ ...filters, excludeBP: e.target.value })
                  }
                  placeholder="P00099"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Grupos SN</label>
                <MultiSelectDropdown
                  options={catalogs.groups}
                  values={filters.groupCodes}
                  onChange={(v) => setFilters({ ...filters, groupCodes: v })}
                  placeholder="Todos los grupos"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Metodos de pago</label>
                <MultiSelectDropdown
                  options={catalogs.paymentMethods}
                  values={filters.paymentMethods}
                  onChange={(v) =>
                    setFilters({ ...filters, paymentMethods: v })
                  }
                  placeholder="Todos los metodos"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Monedas</label>
                <MultiSelectDropdown
                  options={catalogs.currencies}
                  values={filters.currencies}
                  onChange={(v) => setFilters({ ...filters, currencies: v })}
                  placeholder="Todas las monedas"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Importe minimo</label>
                <input
                  type="number"
                  className="form-control"
                  value={filters.minAmount}
                  onChange={(e) =>
                    setFilters({ ...filters, minAmount: e.target.value })
                  }
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Importe maximo</label>
                <input
                  type="number"
                  className="form-control"
                  value={filters.maxAmount}
                  onChange={(e) =>
                    setFilters({ ...filters, maxAmount: e.target.value })
                  }
                />
              </div>
              <div className="col-md-9 d-flex align-items-end gap-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="cn"
                    checked={filters.considerCreditNotes}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        considerCreditNotes: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="cn">
                    Considerar notas de credito
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="ob"
                    checked={filters.onlyWithBank}
                    onChange={(e) =>
                      setFilters({ ...filters, onlyWithBank: e.target.checked })
                    }
                  />
                  <label className="form-check-label" htmlFor="ob">
                    Solo SN con cuenta bancaria
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-3 d-flex justify-content-end">
              <button
                className="btn btn-primary"
                disabled={loading}
                onClick={handleRecommend}
              >
                {loading ? "Buscando..." : "Generar recomendaciones"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Step 1: grid de recomendaciones ---------- */}
      {view === "wizard" && step === 1 && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <strong>{totals.lines}</strong> de{" "}
                <strong>{items.length}</strong> documentos seleccionados /{" "}
                <strong>{totals.cards}</strong> SN / Total{" "}
                <strong>{fmt(totals.total)}</strong>
              </div>
              <div className="btn-group">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleAll(true)}>
                  Marcar todo
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleAll(false)}>
                  Desmarcar todo
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => toggleAllGroups(false)}
                  title="Expandir todos los proveedores"
                >
                  Expandir
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => toggleAllGroups(true)}
                  title="Colapsar todos los proveedores"
                >
                  Colapsar
                </button>
              </div>
            </div>

            <div style={{ maxHeight: "55vh", overflow: "auto" }}>
              <table className="table table-sm table-striped table-hover align-middle">
                <thead className="table-light sticky-top">
                  <tr>
                    <th></th>
                    <th>Codigo SN</th>
                    <th>Nombre</th>
                    <th>Tipo doc.</th>
                    <th>Nro. doc.</th>
                    <th>Fecha</th>
                    <th>Vto.</th>
                    <th>Moneda</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Saldo</th>
                    <th className="text-end">A pagar</th>
                    <th>Banco</th>
                    <th>Cuenta</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const collapsed = !!collapsedGroups[g.cardCode];
                    const allChecked =
                      g.rows.length > 0 && g.checkedCount === g.rows.length;
                    const someChecked =
                      g.checkedCount > 0 && g.checkedCount < g.rows.length;
                    return (
                      <React.Fragment key={g.cardCode}>
                        <tr
                          className="table-secondary"
                          style={{ cursor: "pointer" }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={allChecked}
                              ref={(el) => {
                                if (el) el.indeterminate = someChecked;
                              }}
                              onChange={(e) =>
                                toggleGroupChecked(g.cardCode, e.target.checked)
                              }
                            />
                          </td>
                          <td
                            colSpan={12}
                            onClick={() => toggleGroup(g.cardCode)}
                          >
                            <strong>
                              {collapsed ? "\u25B6" : "\u25BC"} {g.cardCode} -{" "}
                              {g.cardName}
                            </strong>
                            <span className="ms-3 text-muted">
                              {g.checkedCount}/{g.rows.length} docs / Total{" "}
                              {fmt(g.total)} / Saldo {fmt(g.open)} / A pagar{" "}
                              <strong>{fmt(g.pay)}</strong>
                              {g.bankCode ? ` / Banco ${g.bankCode}` : ""}
                              {g.bankAcct ? ` / Cta ${g.bankAcct}` : ""}
                            </span>
                          </td>
                        </tr>
                        {!collapsed &&
                          g.rows.map((it) => (
                            <tr key={`${it.ObjType}-${it.DocEntry}-${it._idx}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={!!it.Checked}
                                  onChange={(e) =>
                                    updateLine(it._idx, {
                                      Checked: e.target.checked,
                                    })
                                  }
                                />
                              </td>
                              <td></td>
                              <td className="text-muted small">
                                {it.CardName}
                              </td>
                              <td>{it.ObjType}</td>
                              <td>{it.DocNum}</td>
                              <td>{(it.DocDate || "").slice(0, 10)}</td>
                              <td>{(it.DueDate || "").slice(0, 10)}</td>
                              <td>{it.DocCur}</td>
                              <td className="text-end">{fmt(it.DocTotal)}</td>
                              <td className="text-end">{fmt(it.OpenAmount)}</td>
                              <td className="text-end" style={{ width: 150 }}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control form-control-sm text-end"
                                  value={fmt(it.PayAmount)}
                                  onChange={(e) => {
                                    const raw = String(e.target.value || "")
                                      .replace(/\./g, "")
                                      .replace(",", ".")
                                      .replace(/[^0-9.\-]/g, "");
                                    updateLine(it._idx, {
                                      PayAmount: Number(raw || 0),
                                    });
                                  }}
                                />
                              </td>
                              <td>{it.BankCode}</td>
                              <td>{it.BankAcct}</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={13} className="text-center text-muted">
                        Sin documentos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-between">
              <button className="btn btn-outline-secondary" onClick={() => (run?.code ? backToList() : setStep(0))}>
                {run?.code ? "Volver al listado" : "Volver"}
              </button>
              <div className="d-flex gap-2">
                {run?.code && (
                  <button
                    className="btn btn-outline-success"
                    disabled={loading}
                    onClick={handleSaveChanges}
                  >
                    {loading ? "Guardando..." : "Guardar cambios"}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  disabled={loading || totals.lines === 0}
                  onClick={() => setStep(2)}
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Step 2: resumen y ejecucion ---------- */}
      {view === "wizard" && step === 2 && (
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Nombre del proceso</label>
                <input
                  className="form-control"
                  value={header.name}
                  onChange={(e) => setHeader({ ...header, name: e.target.value })}
                  placeholder={`PROC-${new Date().toISOString().slice(0, 10)}`}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Fecha pago</label>
                <input
                  type="date"
                  className="form-control"
                  value={header.pmntDate}
                  onChange={(e) =>
                    setHeader({ ...header, pmntDate: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Metodo de pago</label>
                <select
                  className="form-select"
                  value={header.pymCode}
                  onChange={(e) => {
                    const code = e.target.value;
                    const m = catalogs.paymentMethods.find(
                      (x) => String(x.code) === code
                    );
                    setHeader({
                      ...header,
                      pymCode: code,
                      // U_BankAcct -> SL CashAccount: cuenta contable (GL)
                      bankAcct: m?.glAccount || header.bankAcct,
                      bankAccountNo: m?.account || "",
                    });
                  }}
                >
                  <option value="">(seleccione)</option>
                  {catalogs.paymentMethods.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.code} - {p.name}
                      {p.glAccount ? ` [GL ${p.glAccount}]` : ""}
                    </option>
                  ))}
                </select>
                {header.pymCode && (
                  <small className="text-muted d-block">
                    Cuenta bancaria: <strong>{header.bankAccountNo || "-"}</strong>
                  </small>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Cuenta contable de transferencia</label>
                <input
                  className="form-control"
                  value={header.bankAcct}
                  onChange={(e) =>
                    setHeader({ ...header, bankAcct: e.target.value })
                  }
                  placeholder="se autocompleta del metodo (GL)"
                />
                <small className="text-muted">
                  Se enviara como <code>TransferAccount</code> al crear el pago en SAP.
                </small>
              </div>
              <div className="col-md-1">
                <label className="form-label">Moneda</label>
                <select
                  className="form-select"
                  value={header.currency}
                  onChange={(e) =>
                    setHeader({ ...header, currency: e.target.value })
                  }
                >
                  {catalogs.currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Notas</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={header.notes}
                  onChange={(e) =>
                    setHeader({ ...header, notes: e.target.value })
                  }
                />
              </div>
            </div>

            <hr />
            <div className="d-flex flex-wrap gap-4">
              <div>
                <small className="text-muted">Lineas</small>
                <h5>{totals.lines}</h5>
              </div>
              <div>
                <small className="text-muted">Socios de negocio</small>
                <h5>{totals.cards}</h5>
              </div>
              <div>
                <small className="text-muted">Total</small>
                <h5>{fmt(totals.total)}</h5>
              </div>
              {run && (
                <>
                  <div>
                    <small className="text-muted">Codigo del proceso</small>
                    <h5 className="text-primary">{run.code}</h5>
                  </div>
                  <div>
                    <small className="text-muted">Estado</small>
                    <h5>{STATUS_LABEL[run.status] || run.status}</h5>
                  </div>
                </>
              )}
            </div>

            <div className="mt-3 d-flex justify-content-between gap-2 flex-wrap">
              <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                Volver
              </button>
              <div className="d-flex gap-2 flex-wrap">
                {!run && (
                  <>
                    <button
                      className="btn btn-outline-success"
                      disabled={loading || totals.lines === 0}
                      onClick={() => handleCreateRun("DRAFT")}
                      title="Guarda como borrador editable"
                    >
                      {loading ? "Guardando..." : "Guardar borrador"}
                    </button>
                    <button
                      className="btn btn-success"
                      disabled={loading || totals.lines === 0}
                      onClick={() => handleCreateRun("RECOMMENDED")}
                      title="Lo deja listo para que un aprobador lo ejecute"
                    >
                      {loading ? "Guardando..." : "Guardar como recomendacion"}
                    </button>
                  </>
                )}
                {run && run.status !== "EXECUTED" && (
                  <button
                    className="btn btn-warning"
                    disabled={loading}
                    onClick={handleExecute}
                  >
                    {loading ? "Ejecutando..." : "Ejecutar pagos en SAP"}
                  </button>
                )}
                <button className="btn btn-outline-secondary" onClick={backToList}>
                  Volver al listado
                </button>
                <button className="btn btn-outline-primary" onClick={startNewWizard}>
                  Nuevo asistente
                </button>
              </div>
            </div>

            {(() => {
              // Construir resumen amigable a partir del resultado de la ejecucion
              // o de las lineas guardadas (al reabrir un run ejecutado).
              let details = execResult?.details || null;
              if (!details && (run?.status === "EXECUTED" || run?.status === "EXPORTED")) {
                const byCard = {};
                items.forEach((it) => {
                  if (!it.Checked) return;
                  const key = it.CardCode || "";
                  if (!byCard[key]) {
                    byCard[key] = {
                      cardCode: it.CardCode,
                      cardName: it.CardName,
                      ok: it._paymentStat === "CREATED",
                      DocEntry: it._paymentDocE || "",
                      DocNum: it._paymentDocN || "",
                      lines: 0,
                      total: 0,
                      error: it._errorMsg || "",
                    };
                  }
                  byCard[key].lines += 1;
                  byCard[key].total += Number(it.PayAmount || 0);
                  if (it._paymentStat !== "CREATED") byCard[key].ok = false;
                  if (it._errorMsg) byCard[key].error = it._errorMsg;
                });
                details = Object.values(byCard);
              }
              if (!details || details.length === 0) return null;
              const okCount = details.filter((d) => d.ok).length;
              const errCount = details.length - okCount;
              const totalOk = details
                .filter((d) => d.ok)
                .reduce((s, d) => s + Number(d.total || d.SumApplied || 0), 0);
              return (
                <div className="mt-4">
                  <h6 className="mb-2">Pagos generados en SAP</h6>
                  <div className="d-flex gap-3 flex-wrap mb-2">
                    <span className="badge bg-success">
                      {okCount} pago(s) creado(s)
                    </span>
                    {errCount > 0 && (
                      <span className="badge bg-danger">
                        {errCount} con error
                      </span>
                    )}
                    {totalOk > 0 && (
                      <span className="text-muted">
                        Total acreditado: <strong>{fmt(totalOk)}</strong>
                      </span>
                    )}
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Socio de negocio</th>
                          <th className="text-center">Resultado</th>
                          <th>Nro. de pago</th>
                          <th className="text-end">Lineas</th>
                          <th className="text-end">Importe</th>
                          <th>Detalle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.map((d, i) => (
                          <tr key={i} className={d.ok ? "" : "table-danger"}>
                            <td>
                              <div>
                                <strong>{d.cardCode}</strong>
                              </div>
                              {d.cardName && (
                                <small className="text-muted">
                                  {d.cardName}
                                </small>
                              )}
                            </td>
                            <td className="text-center">
                              {d.ok ? (
                                <span className="badge bg-success">
                                  Generado
                                </span>
                              ) : (
                                <span className="badge bg-danger">
                                  Con error
                                </span>
                              )}
                            </td>
                            <td>
                              {d.DocNum ? (
                                <strong>{d.DocNum}</strong>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                              {d.DocEntry && (
                                <div>
                                  <small className="text-muted">
                                    DocEntry: {d.DocEntry}
                                  </small>
                                </div>
                              )}
                            </td>
                            <td className="text-end">{d.lines || ""}</td>
                            <td className="text-end">
                              {d.total != null ? fmt(d.total) : ""}
                            </td>
                            <td style={{ maxWidth: 360 }}>
                              <small className="text-danger">
                                {d.error || ""}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AsistentePagos;
